import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Users, 
  Filter, 
  Search, 
  Home, 
  Heart, 
  Utensils, 
  Truck, 
  Package,
  Info,
  CheckCircle,
  CircleDot,
  XCircle,
  Clock,
  Navigation,
  Plus,
  RefreshCw,
  AlertTriangle,
  Globe
} from 'lucide-react';
import { EmergencyResource } from '../types/resource';
import { ResourceService } from '../services/resourceService';
import { useLocationDetection } from '../hooks/useLocationDetection';
import DirectionsModal from '../components/resources/DirectionsModal';
import CallModal from '../components/resources/CallModal';
import AddResourceModal from '../components/resources/AddResourceModal';
import toast from 'react-hot-toast';

const Resources = () => {
  const { location: userLocation, isLoading: locationLoading, detectLocation } = useLocationDetection();
  const [resources, setResources] = useState<EmergencyResource[]>([]);
  const [filteredResources, setFilteredResources] = useState<EmergencyResource[]>([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showLocationFilter, setShowLocationFilter] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [directionsModal, setDirectionsModal] = useState<{
    isOpen: boolean;
    facility: any;
  }>({ isOpen: false, facility: null });
  const [callModal, setCallModal] = useState<{
    isOpen: boolean;
    facility: any;
  }>({ isOpen: false, facility: null });

  // Auto-detect location on component mount
  useEffect(() => {
    if (!userLocation && !locationLoading) {
      detectLocation();
    }
  }, [userLocation, locationLoading, detectLocation]);

  // Fetch resources
  const fetchResources = async (useLocationFilter = false) => {
    try {
      setIsLoading(true);
      
      const filters: any = {};
      
      // Apply location filter if enabled and user location is available
      if (useLocationFilter && userLocation) {
        filters.countryCode = userLocation.countryCode;
        if (userLocation.region) {
          filters.region = userLocation.region;
        }
      }
      
      const fetchedResources = await ResourceService.fetchResources(filters);
      setResources(fetchedResources);
      
      if (useLocationFilter && userLocation) {
        toast.success(`Found ${fetchedResources.length} resources in ${userLocation.city || userLocation.region || userLocation.country}`);
      }
    } catch (error: any) {
      console.error('Error fetching resources:', error);
      toast.error('Failed to load resources. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchResources();
  }, []);

  // Filter resources based on search and filter criteria
  useEffect(() => {
    let filtered = resources;
    
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(resource => resource.type === selectedFilter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(resource => 
        resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.location_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredResources(filtered);
  }, [resources, selectedFilter, searchTerm]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchResources(showLocationFilter);
    setIsRefreshing(false);
  };

  const handleLocationFilterToggle = () => {
    const newShowLocationFilter = !showLocationFilter;
    setShowLocationFilter(newShowLocationFilter);
    fetchResources(newShowLocationFilter);
  };

  const handleResourceAdded = () => {
    fetchResources(showLocationFilter);
  };

  const filterOptions = [
    { value: 'all', label: 'All Resources' },
    { value: 'shelter', label: 'Shelters' },
    { value: 'medical', label: 'Medical' },
    { value: 'food', label: 'Food & Water' },
    { value: 'supply', label: 'Supplies' },
    { value: 'transport', label: 'Transportation' },
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'shelter': return Home;
      case 'medical': return Heart;
      case 'food': return Utensils;
      case 'supply': return Package;
      case 'transport': return Truck;
      default: return Info;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-accent-green';
      case 'full': return 'text-accent-orange';
      case 'closed': return 'text-accent-red';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return CheckCircle;
      case 'full': return CircleDot;
      case 'closed': return XCircle;
      default: return Info;
    }
  };

  const getAvailabilityPercentage = (available: number, capacity: number) => {
    return Math.round((available / capacity) * 100);
  };

  const getAvailabilityColor = (percentage: number) => {
    if (percentage > 50) return 'bg-accent-green';
    if (percentage > 20) return 'bg-accent-orange';
    return 'bg-accent-red';
  };

  const handleGetDirections = (resource: EmergencyResource) => {
    const facility = {
      name: resource.name,
      location: `${resource.address}, ${resource.city}`,
      coordinates: [resource.longitude, resource.latitude] as [number, number],
      type: resource.type
    };
    setDirectionsModal({ isOpen: true, facility });
  };

  const handleCall = (resource: EmergencyResource) => {
    const facility = {
      name: resource.name,
      contact: resource.contact,
      type: resource.type,
      status: resource.status
    };
    setCallModal({ isOpen: true, facility });
  };

  const getLocationText = () => {
    if (!userLocation) return 'Global';
    if (userLocation.city) return `${userLocation.city}, ${userLocation.country}`;
    if (userLocation.region) return `${userLocation.region}, ${userLocation.country}`;
    return userLocation.country;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-20 pb-8 px-4"
    >
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary-400 to-primary-500 bg-clip-text text-transparent">
                Emergency Resources
              </h1>
              <p className="text-gray-300">
                Find and manage essential services, shelters, medical facilities, and supplies in your area.
              </p>
            </div>
            
            {/* Location Status */}
            <div className="text-right">
              <div className="flex items-center space-x-2 mb-1">
                <div className={`w-2 h-2 rounded-full ${locationLoading ? 'bg-accent-orange animate-pulse' : userLocation ? 'bg-accent-green' : 'bg-gray-400'}`}></div>
                <span className="text-sm text-gray-400">
                  {locationLoading ? 'Detecting...' : showLocationFilter ? getLocationText() : 'Global View'}
                </span>
              </div>
              {userLocation && (
                <div className="text-xs text-gray-500">
                  {filteredResources.length} resources found
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-accent-green mb-1">
              {resources.filter(r => r.status === 'open').length}
            </div>
            <div className="text-sm text-gray-400">Open Facilities</div>
          </div>
          <div className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-primary-400 mb-1">
              {resources.reduce((sum, r) => sum + r.available, 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">Available Spaces</div>
          </div>
          <div className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-accent-orange mb-1">
              {resources.filter(r => r.type === 'medical').length}
            </div>
            <div className="text-sm text-gray-400">Medical Centers</div>
          </div>
          <div className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-accent-yellow mb-1">24/7</div>
            <div className="text-sm text-gray-400">Emergency Line</div>
          </div>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col md:flex-row gap-4 mb-8"
        >
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search resources by name, location, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors"
            />
          </div>

          {/* Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="pl-10 pr-8 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-400 transition-colors appearance-none cursor-pointer min-w-[200px]"
            >
              {filterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Location Filter Toggle */}
          <button
            onClick={handleLocationFilterToggle}
            disabled={!userLocation}
            className={`px-4 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              showLocationFilter 
                ? 'bg-primary-400 text-white' 
                : 'bg-dark-700 border border-dark-600 text-gray-300 hover:bg-dark-600'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={userLocation ? 'Filter by your location' : 'Location detection required'}
          >
            <Globe className="w-4 h-4" />
            {showLocationFilter ? 'Local' : 'Global'}
          </button>

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-gray-300 hover:bg-dark-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          {/* Add Resource Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsAddModalOpen(true)}
            className="bg-gradient-to-r from-primary-400 to-primary-500 px-6 py-3 rounded-lg font-semibold flex items-center gap-2 hover:shadow-lg transition-all duration-200"
          >
            <Plus className="w-4 h-4" />
            Add Resource
          </motion.button>
        </motion.div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary-400/30 border-t-primary-400 rounded-full animate-spin"></div>
          </div>
        ) : (
          /* Resources Grid */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource, index) => {
              const TypeIcon = getTypeIcon(resource.type);
              const StatusIcon = getStatusIcon(resource.status);
              const availabilityPercentage = getAvailabilityPercentage(resource.available, resource.capacity);
              
              return (
                <motion.div
                  key={resource.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ 
                    y: -5,
                    transition: { duration: 0.2 }
                  }}
                  className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-6 hover:border-dark-600 transition-all duration-300 group cursor-pointer"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-primary-400 to-primary-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <TypeIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white group-hover:text-primary-300 transition-colors">
                          {resource.name}
                        </h3>
                        <p className="text-sm text-gray-400 capitalize">{resource.type}</p>
                      </div>
                    </div>
                    <div className={`flex items-center space-x-1 ${getStatusColor(resource.status)}`}>
                      <StatusIcon className="w-4 h-4" />
                      <span className="text-sm font-medium capitalize">{resource.status}</span>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-center space-x-2 mb-3 text-gray-300">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{resource.location_name}</span>
                  </div>

                  {/* Address */}
                  <div className="text-sm text-gray-400 mb-3">
                    {resource.address}, {resource.city}
                    {resource.region && `, ${resource.region}`}
                  </div>

                  {/* Contact */}
                  <div className="flex items-center space-x-2 mb-4 text-gray-300">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{resource.contact}</span>
                  </div>

                  {/* Capacity */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-300">Capacity</span>
                      </div>
                      <span className="text-sm font-medium text-white">
                        {resource.available} / {resource.capacity}
                      </span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-dark-700 rounded-full h-2">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${availabilityPercentage}%` }}
                        transition={{ duration: 1, delay: index * 0.1 + 0.5 }}
                        className={`h-2 rounded-full ${getAvailabilityColor(availabilityPercentage)}`}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>{availabilityPercentage}% available</span>
                      <span>{resource.capacity - resource.available} occupied</span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                    {resource.description}
                  </p>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleGetDirections(resource)}
                      className="flex-1 bg-gradient-to-r from-primary-400 to-primary-500 px-4 py-2 rounded-lg text-white font-medium text-sm hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      <Navigation className="w-4 h-4" />
                      Directions
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleCall(resource)}
                      className="px-4 py-2 border border-dark-600 rounded-lg text-gray-300 font-medium text-sm hover:bg-dark-700 transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </motion.button>
                  </div>

                  {/* Status Indicator */}
                  {resource.status === 'open' && (
                    <div className="mt-3 flex items-center space-x-2 text-xs text-accent-green">
                      <div className="w-2 h-2 bg-accent-green rounded-full animate-pulse"></div>
                      <span>Currently accepting people</span>
                    </div>
                  )}
                  {resource.status === 'full' && (
                    <div className="mt-3 flex items-center space-x-2 text-xs text-accent-orange">
                      <div className="w-2 h-2 bg-accent-orange rounded-full"></div>
                      <span>At capacity - call for waitlist</span>
                    </div>
                  )}
                  {resource.status === 'closed' && (
                    <div className="mt-3 flex items-center space-x-2 text-xs text-accent-red">
                      <div className="w-2 h-2 bg-accent-red rounded-full"></div>
                      <span>Temporarily closed</span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* No Results */}
        {!isLoading && filteredResources.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Resources Found</h3>
            <p className="text-gray-400 mb-4">
              {showLocationFilter && userLocation 
                ? `No resources found in ${getLocationText()}. Try expanding your search or add a new resource.`
                : 'No resources match your search criteria. Try adjusting your filters or add a new resource.'
              }
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsAddModalOpen(true)}
              className="bg-gradient-to-r from-primary-400 to-primary-500 px-6 py-3 rounded-lg font-semibold flex items-center gap-2 mx-auto hover:shadow-lg transition-all duration-200"
            >
              <Plus className="w-4 h-4" />
              Add First Resource
            </motion.button>
          </motion.div>
        )}

        {/* Emergency Contact Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 bg-gradient-to-r from-accent-red/10 to-accent-orange/10 border border-accent-red/30 rounded-xl p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-accent-red rounded-lg flex items-center justify-center">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Emergency Assistance</h3>
                <p className="text-gray-300">Need immediate help? Contact emergency services</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-accent-red mb-1">911</div>
              <div className="text-sm text-gray-400">24/7 Emergency Line</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      <DirectionsModal
        isOpen={directionsModal.isOpen}
        onClose={() => setDirectionsModal({ isOpen: false, facility: null })}
        facility={directionsModal.facility}
      />

      <CallModal
        isOpen={callModal.isOpen}
        onClose={() => setCallModal({ isOpen: false, facility: null })}
        facility={callModal.facility}
      />

      <AddResourceModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onResourceAdded={handleResourceAdded}
      />
    </motion.div>
  );
};

export default Resources;